import ModelFactoryModel, {IModelFactoryModels} from "../ModelFactoryModel";
import {RouteKey, FactoryID} from "../../type-interface/ModelFactoryType";
import {IStringObject, IStringUndefined} from "../../../common/SimpleInterface";
import RouteChoice, {IRouteChoices} from "./RouteChoice";
import RouteFactory from "./RouteFactory";
import {IRouteIDs} from "../../type-interface/ModelFactoryInterface";
import {getManager} from "../../../manager/Manager";
import {cloneObject, createFullHref, idToSlug, resetObject, slugToID} from "../../../common/SiteHelper";
import {dispatchRouteModelUpdate} from "../../../event/DispatchEvent";
import eventBus from "../../../event/EventBus";
import DataModel from "../data/DataModel";
import DataChoice from "../data/DataChoice";
import {getModelFactory} from "../../ModelFactory";


export default class RouteModel extends ModelFactoryModel
{

    navigationRouteKey: RouteKey | null = null;
    nextFactoryID:FactoryID | null = null;

    choices: IRouteChoices = {};
    baseURL:string = "";
    href:string = "";
    realPath:string = "";

    defaultChoiceID:string = "";
    goToFirstChild:boolean = false;
    publicChoiceIDs:IStringUndefined = {};
    allowUnknownIfEmpty:boolean = false;
    allowUnknownChoice:boolean = false;
    sourceDataModel:DataModel | null = null;
    doCreateChoicesFromSource:boolean = false;
    activeUiChoiceList: RouteChoice[]|null = null;
    uiChoices: IRouteChoices = {};
    uiChoiceModelList: RouteModel[] = [];
    defaultChoiceOrder: string[] = [];
    uiChoiceOrder: (string|string[])[]|null = null;
    noTitle:boolean = false;

    routeIDs: IRouteIDs = {};
    params:IStringObject = {};
    hash:string = "";

    constructor(id: string)
    {
        super(id);
        this.clearUiChoiceList = this.clearUiChoiceList.bind(this);
        this.onAnyRouteModelUpdate = this.onAnyRouteModelUpdate.bind(this);
        this.onAnyDataModelUpdate = this.onAnyDataModelUpdate.bind(this);
    }

    choiceList(choiceOrder?:string[]): RouteChoice[]
    {
        return super.choiceList(choiceOrder) as RouteChoice[];
    }

    clearActiveChoiceList(): void
    {
        super.clearActiveChoiceList();
        this.clearUiChoiceList()
    }

    requireChoice(choiceID:string): RouteChoice
    {
        let choice = this.choices[choiceID];
        if(choice){
            return choice;
        }
        choice = this.factory().birthChoice(choiceID);
        choice.id = choiceID;
        this.addChoice(choice);
        return choice
    }

    uiChoiceList(fromOrder?:(string|string[])[]): RouteChoice[]
    {
        if(!this.activeUiChoiceList){
            this.createUiChoiceList(null, true);
        }
        if(!fromOrder){
            return this.activeUiChoiceList!;
        }
        const choiceList:RouteChoice[] = [];
        for(let pathChoiceOrder of fromOrder){
            if(!Array.isArray(pathChoiceOrder)){
                pathChoiceOrder = [pathChoiceOrder]
            }
            let path:string = "";
            for(let pathChoiceID of pathChoiceOrder){
                path += (path ? "&" : "")+pathChoiceID;
            }
            const choice = this.uiChoices[path] ?? null;
            if(choice){
                choiceList.push(choice);
            }
        }
        return choiceList;
    }

    createUiChoiceList(uiChoiceOrder: (string|string[])[]|null = null, saveAsActive:boolean = false): RouteChoice[]
    {
        uiChoiceOrder = uiChoiceOrder ?? this.uiChoiceOrder;
        if(saveAsActive){
            this.uiChoiceOrder = uiChoiceOrder;
            this.clearUiChoiceList();
            if(!uiChoiceOrder){
                this.activeUiChoiceList = this.choiceList();
                for(let choice of this.activeUiChoiceList){
                    this.uiChoices[choice.id] = choice;
                }
                return this.activeUiChoiceList;
            }
            this.activeUiChoiceList = [];
        }

        if(!uiChoiceOrder){
            return [];
        }

        const uiChoiceList: RouteChoice[] = saveAsActive ? (this.activeUiChoiceList ?? []) : [];

        for(let pathChoiceOrder of uiChoiceOrder){
            if(!Array.isArray(pathChoiceOrder)){
                pathChoiceOrder = [pathChoiceOrder]
            }
            let model:RouteModel|null = this;
            let path:string = "";
            for(let i = 0; i < pathChoiceOrder.length; i++){
                const pathChoiceID = pathChoiceOrder[i];
                path += (path ? "&" : "")+pathChoiceID;
                const pathChoice = model!.choices[pathChoiceID] ?? null;
                if(!pathChoice){
                    break;
                }
                if(i === pathChoiceOrder.length - 1){
                    this.uiChoices[path] = pathChoice;

                    if(!uiChoiceList.includes(pathChoice)){
                        uiChoiceList.push(pathChoice);
                    }
                    break;
                }
                model = getManager().navigation.getChoiceNextModel(pathChoice);
                if(!model){
                    break;
                }
                if(!this.uiChoiceModelList.includes(model)){
                    this.uiChoiceModelList.push(model);
                }
            }
        }

        if(saveAsActive){
            eventBus.on("site.route-model-update", this.onAnyRouteModelUpdate);
        }
        return uiChoiceList;
    }

    onAnyRouteModelUpdate(event: CustomEvent):RouteModel|null
    {
        const routeModel:RouteModel|null = event?.detail.routeModel ?? null;
        if(routeModel && this.uiChoiceModelList.includes(routeModel)){
            this.clearUiChoiceList();
        }
        return routeModel;
    }

    onAnyDataModelUpdate(event: CustomEvent):DataModel|null
    {
        const dataModel:DataModel|null = event?.detail.dataModel ?? null;
        if(dataModel && !dataModel.destroyed && dataModel.isPopulated() && !dataModel.populating){
            this.onDataModelPopulated(dataModel)
        }

        return dataModel;
    }

    myDataModels():DataModel[]
    {
        const models:DataModel[] = [];
        if(this.sourceDataModel){
            models.push(this.sourceDataModel);
        }
        return models;

    }

    onDataModelPopulated(dataModel: DataModel): void
    {
        if(this.myDataModels().includes(dataModel)){
            this.applySourceData();
        }
    }

    clearUiChoiceList(): void
    {
        eventBus.remove("site.route-model-update", this.onAnyRouteModelUpdate);
        this.activeUiChoiceList = null;
        this.uiChoices = {};
        this.uiChoiceModelList.length = 0;
    }

    firstVisibleChild():RouteChoice|null
    {
        for(let choice of this.choiceList()){
            if(!choice.hiddenRoute){
                return choice;
            }
        }
        return null;
    }

    parentChoice():RouteChoice|null
    {
        const navigationModel = getManager().navigation.createNavigationModel(this.routeIDs, null, this.params, this.hash);
        for(let i = navigationModel.levelList.length - 1; i > 0; i--){
            if(navigationModel.levelList[i].model.id === this.id){
                return navigationModel.levelList[i - 1].choice;
            }
        }
        return null;
    }

    selectedChoice():RouteChoice|null
    {
        for(let i = getManager().navigation.model.levelList.length - 1; i > 0; i--){
            const level = getManager().navigation.model.levelList[i];
            if(level.model.id === this.id){
                return level.choice;
            }
        }
        return null;
    }

    resetChoiceList()
    {
        super.resetChoiceList();
        this.publicChoiceIDs = {};
    }

    applyChoice(choice: RouteChoice): void
    {
        if(choice.pageID){
            choice.postDataChoice = choice.pageID ? (getModelFactory().pageData.getPageDataModel().choices[choice.pageID] ?? null) : null;
        }
        choice.factoryID = this.factoryID;
        choice.navigationRouteKey = this.navigationRouteKey;
        if(choice.publicID){
            this.publicChoiceIDs[choice.publicID] = choice.id;
        }
        if(!choice.nextFactoryID){
            choice.nextFactoryID = this.nextFactoryID ? this.nextFactoryID : "end_route";
        }
        choice.isFromFirstChildModel = this.goToFirstChild;
        this.applyRouteToChoice(choice);
    }

    onModelConditionsApplied()
    {
        super.onModelConditionsApplied();

        this.applyModelConditionsToRoute();

        const sourceDataModel  = this.requireSourceDataModel()

        if(sourceDataModel){
            this.setSourceDataModel(sourceDataModel);
        }

        if(this.allowUnknownIfEmpty && !Object.keys(this.choices).length && !this.choiceOrder.length){
            this.allowUnknownChoice = true;
        }
        this.onRouteModelChanged();
    }

    initChoiceOrder()
    {
        super.initChoiceOrder();
        this.defaultChoiceOrder = this.choiceOrder.slice()
        this.choiceOrder = this.createChoiceOrder() ?? this.defaultChoiceOrder.slice();
    }

    applyModelConditionsToRoute(){

    }


    requireSourceDataModel(): DataModel|null
    {
        return null;
    }

    getDerivedModels(): ModelFactoryModel[]
    {
        const models = super.getDerivedModels();
        if(this.sourceDataModel){
            models.push(this.sourceDataModel)
        }
        return models;
    }

    applyHasDataModels(): void
    {
        eventBus.remove("site.data-model-update", this.onAnyDataModelUpdate);
        eventBus.on("site.data-model-update", this.onAnyDataModelUpdate);
    }

    setSourceDataModel(sourceDataModel:DataModel): void {
        this.sourceDataModel = sourceDataModel;
        this.applyHasDataModels();
        this.applySourceData();
    }

    applySourceData() {
        this.rebuildChoiceList();
    }

    getSourceChoiceList(){
        return !this.sourceDataModel?.destroyed ? (this.sourceDataModel?.choiceList() ?? []) : [];
    }

    rebuildChoiceList(): void
    {
        this.resetChoiceList();
        if(this.doCreateChoicesFromSource){
            const sourceChoiceList = this.getSourceChoiceList();
            for(let i = 0; i < sourceChoiceList.length; i++){
                const sourceChoice = sourceChoiceList[i];
                const choice = this.factory().birthChoice(sourceChoice.id);
                this.applySourceToChoice(choice, sourceChoice);
                this.addChoice(choice)
            }
        } else {
            const choiceOrder = this.createChoiceOrder() ?? this.defaultChoiceOrder.slice();
            this.publicChoiceIDs = {};
            this.choiceOrder.length = 0;
            if(choiceOrder.length){
                const factory = this.factory();
                const choices = this.cloneChoices(factory.choices, choiceOrder) as IRouteChoices;
                for(let i = 0; i < choiceOrder.length; i++){
                    const choiceID = choiceOrder[i];
                    const choice = choices[choiceID];
                    if(choice){
                        choice.id = choiceID;
                        this.addChoice(choice);
                    }
                }
            }
        }
        this.onRouteModelChanged();
    }

    createChoiceOrder(): string[]|null
    {
        return null;
    }

    applySourceToChoice(choice:RouteChoice, sourceChoice:DataChoice){
        choice.name = sourceChoice.name;
        choice.publicID = slugToID(sourceChoice.slug);
    }

    applyRouteToChoices(){
        for(let choiceID in this.choices){
            const choice:RouteChoice = this.choices[choiceID];
            this.applyRouteToChoice(choice);
        }
    }


    createUnknownChoice(choiceID:string): RouteChoice
    {
        const choice = new RouteChoice(null, choiceID);
        choice.factoryID = this.factoryID;
        choice.nextFactoryID = this.nextFactoryID ? this.nextFactoryID : "end_route";
        choice.name = choiceID;
        this.applyRouteToChoice(choice);
        choice.isUnknown = true;
        return choice;
    }

    applyRouteToChoice(choice:RouteChoice){
        const baseURL = this.baseURL;
        if(!baseURL){
            return;
        }
        resetObject(choice.routeIDs)
        cloneObject(this.routeIDs, choice.routeIDs);
        choice.routeIDs[this.navigationRouteKey!] = choice.id;

        const defaultChoiceID:string = this.getDefaultChoiceID();
        choice.baseURL = baseURL;
        if(choice.id !== defaultChoiceID){
            const publicID = choice.publicID ? choice.publicID : choice.id;
            choice.baseURL += (baseURL !== "/" ? "/" : "") + idToSlug(publicID);
        }
        choice.href = createFullHref(choice.baseURL, this.params, this.hash);
        choice.realPath = ((this.realPath === "/") ? "" : this.realPath) + "/" + choice.id;
    }

    getValidChoiceID(choiceID:string | null):string | null
    {
        return choiceID && this.publicChoiceIDs[choiceID] ? this.publicChoiceIDs[choiceID] as string : choiceID;
    }

    getDefaultChoiceID():string
    {
        if(this.defaultChoiceID){
            return this.defaultChoiceID;
        }
        const preselected = this.navigationRouteKey ? this.populateIDs[this.navigationRouteKey] : null;
        return preselected ? preselected: "";
    }

    factory(): RouteFactory
    {
        return super.factory() as RouteFactory;
    }

    onRouteModelChanged():void
    {
        dispatchRouteModelUpdate(this);
    }

    destroy()
    {
        eventBus.remove("site.data-model-update", this.onAnyDataModelUpdate);
        this.sourceDataModel = null;
        super.destroy();
    }


    afterDestroyed()
    {
        super.afterDestroyed();
        this.onRouteModelChanged();
    }
}

export interface IRouteModels extends IModelFactoryModels
{
    [key: string]: RouteModel;
}